import { Bell, BellOff, Share2, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { Canvas, Collaborator } from "../../../shared/types";
import { Header } from "../components/Header";
import { useWindowSize } from "../hooks/useWindowSize";
import { drawingPrompts, foodEmojis } from "../lib/emojis";

interface HomePageProps {
  username: string;
  userId: number | null;
  onLogout: () => void;
  onCanvasSelect: (id: number) => void;
  canvases: Canvas[];
  onCreateCanvas: () => void;
  onDeleteCanvas: (id: number) => void;
  onInvitationResponse: (id: number, status: "accepted" | "declined") => void;
  onInvite: (id: number) => void;
  onSubscribeToPush: () => Promise<NotificationPermission>;
}

const getCanvasEmoji = (canvasId: number) => {
  return foodEmojis[canvasId % foodEmojis.length];
};

function CanvasList({
  canvases,
  username,
  userId,
  onSelect,
  onDelete,
  onInvite,
  onInvitationResponse,
  iconSize,
}: {
  canvases: Canvas[];
  username: string;
  userId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onInvite: (id: number) => void;
  onInvitationResponse: (id: number, status: "accepted" | "declined") => void;
  iconSize: number;
}) {
  return (
    <div className="list-container scroll-area">
      {canvases.map((canvas: Canvas) => {
        const currentUserCollab = canvas.collaborators.find(
          (c: Collaborator) => c.user_id === userId,
        );

        const isInvitation = currentUserCollab?.status === "pending";
        const isOwner = canvas.owner === username;
        const isPlayable = canvas.collaborators.length > 1;
        const isMyTurn = Number(canvas.current_turn_user_id) === Number(userId);

        const acceptedCollaborators = canvas.collaborators
          .filter((c: Collaborator) => c.status === "accepted" && c.username !== canvas.owner)
          .map((c: Collaborator) => c.username)
          .join(", ");

        const canOpen = isPlayable || isOwner;

        const getStatusText = () => {
          const turnUsername = (canvas.current_turn_username || canvas.owner || "someone").trim();

          if (isMyTurn) {
            if (!isPlayable) return "share this note to start!";

            if (canvas.passer_username && canvas.passer_username !== username)
              return `${canvas.passer_username} passed you a note!`;

            return "your turn!";
          }

          if (canvas.is_sketching) return `${turnUsername} is sketching`;
          return `waiting for ${turnUsername}`;
        };

        return (
          <div key={canvas.id} className="card">
            {isInvitation ? (
              <div className="canvas-item">
                <span className="canvas-title">{getCanvasEmoji(canvas.id)}</span>
                &nbsp;
                <span className="canvas-owner"> from {canvas.owner}</span>
              </div>
            ) : (
              <button
                type="button"
                className={`canvas-item ${!canOpen ? "not-allowed text-secondary" : ""}`}
                style={!canOpen ? { cursor: "not-allowed" } : {}}
                onClick={() => onSelect(canvas.id)}
                disabled={!canOpen}
              >
                <span
                  className={`canvas-title ${isMyTurn && isPlayable ? "turn-indicator" : ""}`}
                  style={
                    isMyTurn && isPlayable ? { animationDelay: `-${(canvas.id * 1.7) % 9}s` } : {}
                  }
                >
                  {getCanvasEmoji(canvas.id)}
                </span>
                &nbsp;
                <span className={`canvas-owner ${isMyTurn && isPlayable ? "is-active" : ""}`}>
                  {getStatusText()}
                  {isPlayable &&
                    !isMyTurn &&
                    acceptedCollaborators &&
                    `, shared with ${acceptedCollaborators}`}
                </span>
              </button>
            )}
            <div className="canvas-actions">
              {isInvitation ? (
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() =>
                      currentUserCollab && onInvitationResponse(currentUserCollab.id, "accepted")
                    }
                    className="btn btn-primary"
                    style={{ borderRadius: "83px 94px 93px 59px / 64px 52px 81px 87px" }}
                  >
                    accept
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      currentUserCollab && onInvitationResponse(currentUserCollab.id, "declined")
                    }
                    className="btn btn-danger"
                    style={{ borderRadius: "58px 60px 84px 65px / 62px 53px 70px 71px" }}
                  >
                    decline
                  </button>
                </div>
              ) : (
                <>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => onDelete(canvas.id)}
                      className="btn-icon"
                      title="delete canvas"
                    >
                      <Trash2 size={iconSize} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onInvite(canvas.id)}
                    className="btn-icon"
                    title="invite user"
                  >
                    <Share2 size={iconSize} />
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
      {canvases.length === 0 && <div className="empty-list-message">no notes yet. start one?</div>}
    </div>
  );
}

export function HomePage({
  username,
  userId,
  onLogout,
  onCanvasSelect,
  canvases,
  onCreateCanvas,
  onDeleteCanvas,
  onInvitationResponse,
  onInvite,
  onSubscribeToPush,
}: HomePageProps) {
  const [permission, setPermission] = useState(
    "Notification" in window ? Notification.permission : "default",
  );
  const { isMobile } = useWindowSize();
  const iconSize = isMobile ? 18 : 14;
  const headerIconSize = isMobile ? 20 : 16;

  const isGranted = permission === "granted";
  const isDenied = permission === "denied";

  const handleSubscribe = async () => {
    const newPermission = await onSubscribeToPush();
    setPermission(newPermission);
  };

  const getTitle = () => {
    if (isGranted) return "notifications enabled";
    if (isDenied) return "notifications blocked";
    return "enable notifications";
  };

  const prompt = useMemo(
    () => drawingPrompts[Math.floor(Math.random() * drawingPrompts.length)],
    [],
  );

  return (
    <div className="page-container">
      <Header>
        <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
          <h1 className="header-title">pass the note</h1>
          <button type="button" onClick={handleSubscribe} className="btn-icon" title={getTitle()}>
            <div style={{ position: "relative", width: headerIconSize, height: headerIconSize }}>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity 0.3s cubic-bezier(0.2, 0, 0, 1), transform 0.3s cubic-bezier(0.2, 0, 0, 1), filter 0.3s cubic-bezier(0.2, 0, 0, 1)",
                  opacity: isGranted ? 1 : 0,
                  transform: isGranted ? "scale(1)" : "scale(0.25)",
                  filter: isGranted ? "blur(0px)" : "blur(4px)",
                }}
              >
                <Bell size={headerIconSize} />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity 0.3s cubic-bezier(0.2, 0, 0, 1), transform 0.3s cubic-bezier(0.2, 0, 0, 1), filter 0.3s cubic-bezier(0.2, 0, 0, 1)",
                  opacity: isGranted ? 0 : 1,
                  transform: isGranted ? "scale(0.25)" : "scale(1)",
                  filter: isGranted ? "blur(4px)" : "blur(0px)",
                }}
              >
                <BellOff size={headerIconSize} />
              </div>
            </div>
          </button>
        </div>
        <div className="user-info">
          <span>{username}</span>
          <button type="button" onClick={onLogout} className="btn-text">
            logout
          </button>
        </div>
      </Header>
      <main className="home-main">
        <div className="new-note-section">
          <p className="drawing-prompt">...{prompt}?</p>
          <button
            type="button"
            onClick={onCreateCanvas}
            className="btn btn-primary btn-narrow"
            style={{ borderRadius: "99px 75px 64px 94px / 71px 62px 51px 67px" }}
          >
            + new note
          </button>
        </div>
        <CanvasList
          canvases={canvases}
          username={username}
          userId={userId}
          onSelect={onCanvasSelect}
          onDelete={onDeleteCanvas}
          onInvite={onInvite}
          onInvitationResponse={onInvitationResponse}
          iconSize={iconSize}
        />
      </main>
    </div>
  );
}
